import { useCallback, useContext, useState } from 'react';
import { Control, useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { useUpdateItemQuantitiesMutation } from 'redux/api/ordersApi';
import { useAppSelector } from 'redux/hooks';

import { SingleProductContext } from 'contexts';

type UseScalesOrderReturn = {
  isInOrder: boolean;
  control: Control;
  savingInputName: string | null;
  onQuantityBlur: (size: string, newQuantity: number) => Promise<void>;
};

const useScalesOrder = (): UseScalesOrderReturn => {
  const { t } = useTranslation();
  const { control, setValue } = useForm();
  const { productOrder, productOrderSet } = useContext(SingleProductContext);
  const { orderId } = useAppSelector((state) => state.settings);

  const [savingInputName, setSavingInputName] = useState<string | null>(null);
  const [updateItemQuantities] = useUpdateItemQuantitiesMutation();

  const isInOrder = Boolean(productOrder?.available && productOrder?.itemId);

  const onQuantityBlur = useCallback(
    async (size: string, newQuantity: number): Promise<void> => {
      if (!isInOrder || !orderId || !productOrder?.itemId || savingInputName) {
        return;
      }

      const currentSizes = productOrder.sizes ?? [];
      const currentSize = currentSizes.find((s) => s.size === size);

      if (currentSize && currentSize.quantity === newQuantity) {
        return;
      }

      const saveToastId = toast.loading(t('orderTable.savingQty'));
      try {
        setSavingInputName(size);
        const res = await updateItemQuantities({
          orderId,
          itemId: productOrder.itemId,
          size,
          quantity: newQuantity,
        }).unwrap();

        productOrderSet((state) => ({
          ...state,
          sizes: res.item.sizes,
        }));

        toast.success(t('orderTable.savedQty'), {
          id: saveToastId,
        });
      } catch (err: unknown) {
        const error = err as {
          data?: { message?: string; apierror?: { message?: string } };
          error?: string;
        };
        const message =
          error.data?.message ||
          error.data?.apierror?.message ||
          error.error ||
          t('orderTable.errorSavingQty');
        toast.error(message, { id: saveToastId });
        // Reset the field to the last saved value so the input doesn't
        // show the rejected quantity as if it were confirmed.
        const previousQuantity = currentSize?.quantity ?? 0;
        setValue(`scale-${size}`, previousQuantity === 0 ? '' : previousQuantity);
      } finally {
        setSavingInputName(null);
      }
    },
    [
      isInOrder,
      orderId,
      productOrder?.itemId,
      productOrder?.sizes,
      savingInputName,
      t,
      updateItemQuantities,
      productOrderSet,
    ]
  );

  return {
    isInOrder,
    control,
    savingInputName,
    onQuantityBlur,
  };
};

export { useScalesOrder };
